import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Field, Input } from "@dishfolio/ui";
import { authClient } from "../lib/auth.ts";
import { Logo } from "../components/Logo.tsx";
import { rememberPlanIntent } from "../lib/intent.ts";
import { api } from "../lib/api.ts";

/** Sign-in methods the server has configured (Google needs OAuth credentials). */
function useAuthProviders() {
  return useQuery({ queryKey: ["auth-providers"], queryFn: () => api.get<{ email: boolean; google: boolean }>("/public/auth-providers"), staleTime: Infinity, retry: false });
}

const GOOGLE_ERRORS: Record<string, string> = {
  access_denied: "Google sign-in was cancelled.",
  account_not_linked: "This email already has an account. Sign in with your password first.",
  banned: "This account has been suspended. Contact support@dishfolio.app.",
};

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="size-4" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/** "Continue with Google" plus an "or" divider; renders nothing when Google isn't configured. */
function GoogleSignIn({ redirect, newUser }: { redirect?: string; newUser: string }) {
  const providers = useAuthProviders();
  const [loading, setLoading] = useState(false);
  if (!providers.data?.google) return null;
  const start = async () => {
    setLoading(true);
    const res = await authClient.signIn.social({ provider: "google", callbackURL: redirect ?? "/", newUserCallbackURL: newUser, errorCallbackURL: "/login" });
    if (res.error) setLoading(false);
  };
  return (
    <div className="grid gap-4">
      <Button type="button" variant="outline" onClick={start} loading={loading}>
        <GoogleIcon /> Continue with Google
      </Button>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}

export function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children?: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-gradient-to-b from-accent/60 to-background px-4 py-12">
      <Link to="/" className="mb-8">
        <Logo className="text-xl" />
      </Link>
      <Card className="w-full max-w-sm gap-5 p-6 shadow-md">
        <div>
          <h1 className="font-display text-xl font-semibold">{title}</h1>
          {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
        </div>
        {children}
      </Card>
      {footer ? <div className="mt-6 text-sm text-muted-foreground">{footer}</div> : null}
    </div>
  );
}

function safeRedirect(r: string | undefined) {
  return r && r.startsWith("/") && !r.startsWith("//") ? r : "/";
}

export function LoginPage() {
  const search = useSearch({ from: "/login" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(search.error ? (GOOGLE_ERRORS[search.error] ?? "Google sign-in failed. Please try again.") : null);
  const [loading, setLoading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await authClient.signIn.email({ email, password });
    setLoading(false);
    if (res.error) {
      setError(res.error.status === 403 ? "Please verify your email address first. Check your inbox." : (res.error.message ?? "Sign in failed"));
      return;
    }
    await qc.invalidateQueries({ queryKey: ["me"] });
    await navigate({ to: safeRedirect(search.redirect) });
  };
  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to manage your menus."
      footer={
        <>
          New to Dishfolio?{" "}
          <Link to="/signup" search={{ redirect: search.redirect }} className="font-medium text-primary hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <GoogleSignIn redirect={safeRedirect(search.redirect)} newUser="/onboarding" />
      <form onSubmit={submit} className="grid gap-4">
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <Button type="submit" loading={loading}>
          Sign in
        </Button>
        <Link to="/forgot-password" className="text-center text-sm text-muted-foreground hover:text-foreground">
          Forgot your password?
        </Link>
      </form>
    </AuthLayout>
  );
}

export function SignupPage() {
  const search = useSearch({ from: "/signup" });
  useEffect(() => rememberPlanIntent(search.plan), [search.plan]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await authClient.signUp.email({ name, email, password, callbackURL: "/onboarding" });
    setLoading(false);
    if (res.error) {
      setError(res.error.message ?? "Sign up failed");
      return;
    }
    if (!res.data?.token) {
      await navigate({ to: "/verify-email" });
      return;
    }
    await qc.invalidateQueries({ queryKey: ["me"] });
    await navigate({ to: search.redirect ? safeRedirect(search.redirect) : "/onboarding" });
  };
  return (
    <AuthLayout
      title="Create your account"
      subtitle={search.plan ? `Create your account, set up your restaurant, then finish upgrading to ${search.plan === "pro" ? "Pro" : "Business"}.` : "Publish your first menu in minutes. Free forever for one menu."}
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" search={{ redirect: search.redirect }} className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <GoogleSignIn redirect={search.redirect ? safeRedirect(search.redirect) : "/"} newUser={search.redirect ? safeRedirect(search.redirect) : "/onboarding"} />
      <form onSubmit={submit} className="grid gap-4">
        <Field label="Your name" htmlFor="name">
          <Input id="name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password" hint="At least 8 characters.">
          <Input id="password" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <Button type="submit" loading={loading}>
          Create account
        </Button>
        <p className="text-center text-xs text-muted-foreground">By signing up you agree to the Terms and Privacy Policy.</p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` });
    setLoading(false);
    setSent(true);
  };
  return (
    <AuthLayout title="Reset your password" subtitle="We’ll email you a link to choose a new password." footer={<Link to="/login" className="font-medium text-primary hover:underline">Back to sign in</Link>}>
      {sent ? (
        <p className="text-sm">If an account exists for {email}, a reset link is on its way.</p>
      ) : (
        <form onSubmit={submit} className="grid gap-4">
          <Field label="Email" htmlFor="email">
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Button type="submit" loading={loading}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const search = useSearch({ from: "/reset-password" });
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(search.error ? "This reset link is invalid or expired." : null);
  const [loading, setLoading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!search.token) return setError("Missing reset token");
    setLoading(true);
    const res = await authClient.resetPassword({ newPassword: password, token: search.token });
    setLoading(false);
    if (res.error) return setError(res.error.message ?? "Could not reset password");
    await navigate({ to: "/login" });
  };
  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} className="grid gap-4">
        <Field label="New password" htmlFor="password" hint="At least 8 characters.">
          <Input id="password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <Button type="submit" loading={loading} disabled={!search.token}>
          Update password
        </Button>
      </form>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <AuthLayout title="Check your inbox" subtitle="We sent you a link to confirm your email address. Click it to continue." footer={<Link to="/login" className="font-medium text-primary hover:underline">Back to sign in</Link>}>
      <form
        className="grid gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          await authClient.sendVerificationEmail({ email, callbackURL: "/onboarding" });
          setSent(true);
        }}
      >
        <Field label="Didn’t get it? Resend to" htmlFor="email">
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Button type="submit" variant="outline">
          {sent ? "Sent again" : "Resend email"}
        </Button>
      </form>
    </AuthLayout>
  );
}
